/**
 * Unity Lifecycle Methods Helper
 * Hooks and utilities for managing Unity game lifecycle callbacks
 */

import { useLayoutEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FacilitatorRoutes, GamerRoutes } from '../../../config/routes/routeEnums'

// UnityInstance type from react-unity-webgl
export type UnityInstance = {
  SetFullscreen: (fullscreen: number) => void
  // Standard Unity WebGL bridge: call a C# method on a named GameObject. Value must be a string/number.
  SendMessage: (objectName: string, methodName: string, value?: string | number) => void
}

export type UnityContentWindow = Window & {
  unityInstance?: UnityInstance
  onGameLoaded?: () => void
  onGameEnded?: () => void
  onGameAborted?: () => void
  onTutorialAborted?: () => void
  onTutorialEnded?: () => void
  onLobbyLeft?: () => void
}

const POLL_INTERVAL_MS = 25

/** What the game page does when a game ends, and where the player goes next. */
export interface AfterGameHandlers {
  /** Leaves the game's call. */
  onGameComplete?: () => void
  /** Brings the team call back on the way to the feedback; the AI debrief page runs a call of its own. */
  onRejoinCall?: () => void
  resolveDebriefRoute?: () => Promise<string | undefined>
}

const resolvePostGameRoute = (isFacilitatorContext: boolean, isLatestSessionFeedbackEnabled: boolean): string => {
  if (isFacilitatorContext) return FacilitatorRoutes.DASHBOARD
  // The unseen achievements page goes on to the feedback when there is nothing new to show.
  if (isLatestSessionFeedbackEnabled) return GamerRoutes.UNSEEN_ACHIEVEMENTS
  return GamerRoutes.DASHBOARD
}

/**
 * Hook to manage Unity lifecycle methods
 * Attaches callbacks to iframe window for Unity to call
 */
export function useLifeCycleMethods(
  gameFrame: React.RefObject<HTMLIFrameElement | null>,
  _sessionId: string,
  navigate: ReturnType<typeof useNavigate>,
  setShowFullScreenButton: (show: boolean) => void,
  isFacilitatorContext: boolean,
  isLatestSessionFeedbackEnabled: boolean,
  afterGame: AfterGameHandlers = {}
): { onIframeLoad: () => void; isGameLoaded: boolean; leaveGame: () => void } {
  const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const isAttachedRef = useRef(false)
  const [isGameLoaded, setIsGameLoaded] = useState(false)
  // Unity keeps the callbacks attached at load, so the end of the game reads the latest handlers through a ref.
  const afterGameRef = useRef(afterGame)
  afterGameRef.current = afterGame

  const dashboardRoute = isFacilitatorContext ? FacilitatorRoutes.DASHBOARD : GamerRoutes.DASHBOARD
  const postGameRoute = resolvePostGameRoute(isFacilitatorContext, isLatestSessionFeedbackEnabled)

  // A player who quits part-way leaves the usual way.
  const leaveAbortedGame = () => {
    afterGameRef.current.onGameComplete?.()
    afterGameRef.current.onRejoinCall?.()
    navigate(postGameRoute)
  }

  // How a game ends for this page, whether Unity reports it or an operator closed the session from the Back Office.
  // An AI debrief that is about to start, or already has, is where the player goes next, without the team call.
  const leaveGame = async () => {
    afterGameRef.current.onGameComplete?.()
    const debriefRoute = await afterGameRef.current.resolveDebriefRoute?.()
    if (!debriefRoute) afterGameRef.current.onRejoinCall?.()
    navigate(debriefRoute ?? postGameRoute)
  }

  const attachCallbacks = (gameWindow: UnityContentWindow) => {
    gameWindow.onGameLoaded = () => {
      setShowFullScreenButton(true)
      setIsGameLoaded(true)
    }
    gameWindow.onGameEnded = leaveGame
    gameWindow.onGameAborted = leaveAbortedGame
    gameWindow.onTutorialAborted = () => {
      navigate(dashboardRoute)
    }
    gameWindow.onTutorialEnded = () => {
      navigate(dashboardRoute)
    }
    gameWindow.onLobbyLeft = () => {
      navigate(dashboardRoute)
    }
    isAttachedRef.current = true
  }

  useLayoutEffect(() => {
    const tryAttach = () => {
      if (isAttachedRef.current) return true
      const gameWindow = gameFrame.current?.contentWindow as UnityContentWindow | undefined
      if (!gameWindow) return false
      attachCallbacks(gameWindow)
      return true
    }

    if (!tryAttach()) {
      intervalRef.current = setInterval(() => {
        if (tryAttach() && intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = undefined
        }
      }, POLL_INTERVAL_MS)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = undefined
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameFrame, isFacilitatorContext, _sessionId, postGameRoute, dashboardRoute])

  const onIframeLoad = () => {
    if (isAttachedRef.current) return
    const gameWindow = gameFrame.current?.contentWindow as UnityContentWindow | undefined
    if (gameWindow) {
      attachCallbacks(gameWindow)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = undefined
      }
    }
  }

  return { onIframeLoad, isGameLoaded, leaveGame }
}

/**
 * Get Unity instance from iframe
 */
export function getUnityInstance(gameFrame?: React.RefObject<HTMLIFrameElement | null>): UnityInstance | undefined {
  const contentWindow = gameFrame?.current?.contentWindow as UnityContentWindow | undefined
  return contentWindow?.unityInstance
}

/**
 * Get iframe window object
 */
export function getGameFrameWindow(gameFrame?: React.RefObject<HTMLIFrameElement | null>): Window | undefined {
  return gameFrame?.current?.contentWindow || undefined
}
